import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiArrowRight } from 'react-icons/fi';
import Layout from '../Layout/Layout';
import myAxios from '../AxiosUrl';
import styles from './Blog.module.css';

function BlogList() {
    const navigate = useNavigate();
    const uid = localStorage.getItem('id');

    const [blogs, setBlogs] = useState([]);
    const [pagination, setPagination] = useState({});
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [statusFilter, setStatusFilter] = useState('all');
    const [search, setSearch] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [deleting, setDeleting] = useState(null);
    const [toast, setToast] = useState(null);
    const [storeUrl, setStoreUrl] = useState('');

    const showToast = (msg, type = 'success') => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 3000);
    };

    const fetchBlogs = useCallback(async () => {
        setLoading(true);
        try {
            const res = await myAxios.get(`/blogs/${uid}`, {
                params: { page, limit: 10, status: statusFilter, search },
            });
            if (res.data.success) {
                setBlogs(res.data.blogs);
                setPagination(res.data.pagination);
            }
        } catch {
            showToast('Failed to load blog posts', 'error');
        } finally {
            setLoading(false);
        }
    }, [uid, page, statusFilter, search]);

    useEffect(() => { fetchBlogs(); }, [fetchBlogs]);

    useEffect(() => {
        myAxios.get(`/get-domain-new/${uid}`).then((res) => {
            if (res.data?.domain) {
                setStoreUrl(`https://${res.data.domain}`);
            } else if (res.data?.SubDomain) {
                setStoreUrl(`https://${res.data.SubDomain}.ecomtechbd.com`);
            }
        }).catch(() => {});
    }, [uid]);

    const handleDelete = async (id, title) => {
        if (!window.confirm(`Delete "${title}"? This cannot be undone.`)) return;
        setDeleting(id);
        try {
            const res = await myAxios.delete(`/blog/${id}`);
            if (res.data.success) {
                showToast('Blog post deleted');
                fetchBlogs();
            } else {
                showToast(res.data.message || 'Delete failed', 'error');
            }
        } catch {
            showToast('Delete failed', 'error');
        } finally {
            setDeleting(null);
        }
    };

    const handleSearch = (e) => {
        e.preventDefault();
        setPage(1);
        setSearch(searchInput);
    };

    const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

    return (
        <Layout>
            <div className={styles.container}>
                {toast && (
                    <div className={`${styles.toast} ${styles['toast_' + toast.type]}`}>
                        {toast.msg}
                    </div>
                )}

                {/* Header */}
                <div className={styles.pageHeader}>
                    <h1 className={styles.pageTitle}>Blog Posts</h1>
                    <button className={styles.btnPrimary} onClick={() => navigate('/blog/add')}>
                        + New Post
                    </button>
                </div>

                {/* Filters */}
                <div className={styles.filters}>
                    <form className={styles.searchForm} onSubmit={handleSearch}>
                        <input
                            type="text"
                            className={styles.searchInput}
                            placeholder="Search by title..."
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                        />
                        <button type="submit" className={styles.btnSecondary}>Search</button>
                    </form>
                    <div className={styles.statusTabs}>
                        {['all', 'published', 'draft'].map((s) => (
                            <button
                                key={s}
                                className={`${styles.statusTab} ${statusFilter === s ? styles.statusTabActive : ''}`}
                                onClick={() => { setStatusFilter(s); setPage(1); }}
                            >
                                {s.charAt(0).toUpperCase() + s.slice(1)}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Table */}
                {loading ? (
                    <>
                        {/* Skeleton Table */}
                        <div className={styles.skeletonTable}>
                            <div className={styles.skeletonTableHead}>
                                <div className={styles.skeleton} style={{width: '50px', height: '12px'}}></div>
                                <div className={styles.skeleton} style={{width: '60px', height: '12px'}}></div>
                                <div className={styles.skeleton} style={{width: '45px', height: '12px'}}></div>
                                <div className={styles.skeleton} style={{width: '35px', height: '12px'}}></div>
                                <div className={styles.skeleton} style={{width: '40px', height: '12px'}}></div>
                                <div className={styles.skeleton} style={{width: '55px', height: '12px'}}></div>
                            </div>
                            {[...Array(6)].map((_, i) => (
                                <div key={i} className={styles.skeletonTableRow}>
                                    <div className={styles.skeletonTitleCell}>
                                        <div className={styles.skeleton} style={{width: '52px', height: '38px', borderRadius: '6px', flexShrink: 0}}></div>
                                        <div style={{flex: 1}}>
                                            <div className={styles.skeleton} style={{width: '80%', height: '14px', marginBottom: '6px'}}></div>
                                            <div className={styles.skeleton} style={{width: '50%', height: '10px'}}></div>
                                        </div>
                                    </div>
                                    <div className={styles.skeleton} style={{width: '70%', height: '14px'}}></div>
                                    <div className={styles.skeleton} style={{width: '60px', height: '22px', borderRadius: '20px'}}></div>
                                    <div className={styles.skeleton} style={{width: '75%', height: '14px'}}></div>
                                    <div className={styles.skeleton} style={{width: '30px', height: '14px'}}></div>
                                    <div className={styles.skeletonActions}>
                                        <div className={styles.skeleton} style={{width: '50px', height: '28px', borderRadius: '6px'}}></div>
                                        <div className={styles.skeleton} style={{width: '56px', height: '28px', borderRadius: '6px'}}></div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </>
                ) : blogs.length === 0 ? (
                    <div className={styles.emptyState}>
                        <p>No blog posts found.</p>
                        <button className={styles.btnPrimary} onClick={() => navigate('/blog/add')}>
                            Create your first post
                        </button>
                    </div>
                ) : (
                    <>
                        <div className={styles.tableWrap}>
                            <table className={styles.table}>
                                <thead>
                                    <tr>
                                        <th>Title</th>
                                        <th>Category</th>
                                        <th>Status</th>
                                        <th>Date</th>
                                        <th>Views</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {blogs.map((blog) => (
                                        <tr key={blog._id}>
                                            <td>
                                                <div className={styles.blogTitleCell}>
                                                    {blog.coverImage && (
                                                        <img src={blog.coverImage} alt="" className={styles.blogThumb} />
                                                    )}
                                                    <div>
                                                        <div className={styles.blogTitle}>{blog.title}</div>
                                                        <div className={styles.blogSlug}>/{blog.slug}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>{blog.category || '—'}</td>
                                            <td>
                                                <span className={`${styles.badge} ${styles['badge_' + blog.status]}`}>
                                                    {blog.status}
                                                </span>
                                            </td>
                                            <td>{formatDate(blog.publishedAt || blog.created_at)}</td>
                                            <td>{blog.views || 0}</td>
                                            <td>
                                                <div className={styles.actionBtns}>
                                                    {storeUrl && blog.status === 'published' && (
                                                        <a
                                                            className={styles.btnView}
                                                            href={`${storeUrl}/blog/${blog.slug}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            title="View post"
                                                        >
                                                            View
                                                        </a>
                                                    )}
                                                    <button
                                                        className={styles.btnEdit}
                                                        onClick={() => navigate(`/blog/edit/${blog._id}`)}
                                                    >
                                                        Edit
                                                    </button>
                                                    <button
                                                        className={styles.btnDelete}
                                                        disabled={deleting === blog._id}
                                                        onClick={() => handleDelete(blog._id, blog.title)}
                                                    >
                                                        {deleting === blog._id ? '...' : 'Delete'}
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        {pagination.pages > 1 && (
                            <div className={styles.pagination}>
                                <button
                                    className={styles.pageBtn}
                                    disabled={page <= 1}
                                    onClick={() => setPage(p => p - 1)}
                                >
                                    <FiArrowLeft style={{verticalAlign:'middle'}} /> Prev
                                </button>
                                <span className={styles.pageInfo}>
                                    Page {page} of {pagination.pages}
                                </span>
                                <button
                                    className={styles.pageBtn}
                                    disabled={page >= pagination.pages}
                                    onClick={() => setPage(p => p + 1)}
                                >
                                    Next <FiArrowRight style={{verticalAlign:'middle'}} />
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>
        </Layout>
    );
}

export default BlogList;
